import { useSubscription } from '@apollo/client'
import { captureException } from '@sentry/react'
import { isUndefined } from 'lodash'
import { createContext, useContext, useEffect, useMemo } from 'react'
// eslint-disable-next-line no-unused-vars
import { z } from 'zod'

import openNotification from 'src/components/common/notification'
import Spinner from 'src/components/common/spinner'
import { groupBillingState } from 'src/lib/subscription/group-billing-state'
import { BILLING_DATA } from 'src/schema/subscriptions'
import logger from 'src/utils/logger'

/** @type {ReturnType<typeof createContext<Array<z.infer<typeof import('src/lib/zod-schemas/organization-subscription.schema').BillingStateSchema>> |null>>} */
const SubscriptionContext = createContext(null)

SubscriptionContext.displayName = 'SubscriptionContext'

const SubscriptionProvider = ({ children }) => {
    const { data, loading, error } = useSubscription(BILLING_DATA)

    /** @type {Array<z.infer<typeof import('src/lib/zod-schemas/organization-subscription.schema').BillingStateSchema>>} */
    const ctx = useMemo(() => {
        /** @type {SubscriptionState} */
        let ctx__ = []

        if (loading) {
            return ctx__
        }

        if (error) {
            return ctx__
        }

        if (!data.rows || data.rows.length < 1) {
            return ctx__
        }

        const rows = data.rows

        const grouped = rows.reduce((acc, r) => {
            if (acc[r.subscription_id]) {
                acc[r.subscription_id].push(r)
            } else {
                acc[r.subscription_id] = [r]
            }
            return acc
        }, {})

        const billingStates = Object.entries(grouped).map(([_k, v]) => {
            return groupBillingState(v)
        })

        ctx__ = billingStates

        return ctx__
    }, [data, error, loading])

    useEffect(() => {
        if (error) {
            logger.error('an error occurred while fetching subscription data')
            logger.error(error)
            captureException(error, {
                extra: {
                    message:
                        'an error occurred while fetching subscription data',
                },
            })
            openNotification(
                'error',
                `Whoops we didn't expect this, please try again`,
                4
            )
        }
    }, [error])

    if (error) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center">
                <p className="m-0 text-2xl">
                    Whoops, we didn't expect this. Please try refreshing this
                    page!
                </p>
            </div>
        )
    }

    return (
        <SubscriptionContext.Provider value={ctx}>
            {loading ? <Spinner /> : children}
        </SubscriptionContext.Provider>
    )
}

const useSubscriptionCtx = () => {
    const ctx = useContext(SubscriptionContext)

    if (isUndefined(ctx)) {
        throw new Error('Please wrap in BillingContext.Provider')
    }

    return ctx
}

export { useSubscriptionCtx, SubscriptionProvider }
